import type {
  BusinessCenterBrandingEventRecord,
  BusinessCenterBrandingProfileRecord,
  BusinessCenterCompanyRecord,
  BusinessCenterEntityId,
} from "../../lib/types";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";
import { loadBusinessCenterCompanies } from "./businessCenterCompanyUsersData";
import { loadBusinessCenterOnboardingRecords } from "./businessCenterOnboardingData";

export interface BusinessCenterBrandingPreviewSnapshot {
  portal: {
    title: string;
    subtitle: string;
    welcomeMessage: string;
    colors: { primary: string; secondary: string; accent: string };
  };
  login: {
    title: string;
    subtitle: string;
    accent: string;
  };
  dashboard: {
    title: string;
    subtitle: string;
    badge: string;
  };
  email: {
    subject: string;
    headerColor: string;
    footerColor: string;
    signature: string;
  };
}

export interface BusinessCenterBrandingDashboard {
  totalProfiles: number;
  activeDomains: number;
  portalReady: number;
  logoReady: number;
  hidePoweredByCount: number;
}

export interface CreateBusinessCenterBrandingProfileInput {
  companyId: BusinessCenterEntityId;
  companyName: string;
  companySlug: string;
  publicDisplayName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  contactEmail: string;
  supportEmail: string;
  phoneNumber: string;
  website: string;
  companyDescription: string;
  portal: BusinessCenterBrandingProfileRecord["portal"];
  documents: BusinessCenterBrandingProfileRecord["documents"];
  email: BusinessCenterBrandingProfileRecord["email"];
  domains: BusinessCenterBrandingProfileRecord["domains"];
  toggles: BusinessCenterBrandingProfileRecord["toggles"];
  sourceOnboardingId?: BusinessCenterEntityId | null;
}

const brandingProfilesStore = createBusinessCenterOperationalStateStore<BusinessCenterBrandingProfileRecord[]>({
  moduleKey: "business_center_branding_profiles",
  scope: { scopeKey: "branding-center", scopeType: "platform" },
  seed: createSeedBrandingProfiles,
  normalize: (records) => records.map(normalizeBrandingProfile),
  isEmpty: (records) => !Array.isArray(records) || records.length === 0,
});

export function loadBusinessCenterBrandingProfiles(): BusinessCenterBrandingProfileRecord[] {
  return brandingProfilesStore.read();
}

export async function refreshBusinessCenterBrandingProfiles() {
  return brandingProfilesStore.refresh();
}

export function saveBusinessCenterBrandingProfiles(records: BusinessCenterBrandingProfileRecord[]) {
  brandingProfilesStore.write(records);
}

export function upsertBusinessCenterBrandingProfile(nextProfile: BusinessCenterBrandingProfileRecord) {
  const current = loadBusinessCenterBrandingProfiles();
  const existing = current.find((profile) => profile.id === nextProfile.id) || null;
  const normalized = normalizeBrandingProfile({
    ...nextProfile,
    events: appendBrandingEvents(existing?.events || [], existing, nextProfile),
    updatedAt: new Date().toISOString(),
  });
  const exists = current.some((profile) => profile.id === normalized.id);
  const records = exists
    ? current.map((profile) => (profile.id === normalized.id ? normalized : profile))
    : [normalized, ...current];
  saveBusinessCenterBrandingProfiles(records);
  return records;
}

export function updateBusinessCenterBrandingProfile(
  companyId: BusinessCenterEntityId,
  changes: Partial<BusinessCenterBrandingProfileRecord>,
) {
  const current = loadBusinessCenterBrandingProfiles();
  const records = current.map((profile) => {
    if (profile.companyId !== companyId) return profile;
    const next = normalizeBrandingProfile({
      ...profile,
      ...changes,
      events: appendBrandingEvents(profile.events, profile, { ...profile, ...changes }),
      updatedAt: new Date().toISOString(),
    });
    return next;
  });
  saveBusinessCenterBrandingProfiles(records);
  return records;
}

export function createBusinessCenterBrandingProfile(
  input: CreateBusinessCenterBrandingProfileInput,
): BusinessCenterBrandingProfileRecord {
  const now = new Date().toISOString();
  return normalizeBrandingProfile({
    id: `branding-${input.companyId}`,
    companyId: input.companyId,
    companyName: input.companyName,
    publicDisplayName: input.publicDisplayName,
    logoUrl: input.logoUrl,
    faviconUrl: input.faviconUrl,
    primaryColor: input.primaryColor,
    secondaryColor: input.secondaryColor,
    accentColor: input.accentColor,
    contactEmail: input.contactEmail,
    supportEmail: input.supportEmail,
    phoneNumber: input.phoneNumber,
    website: input.website,
    companyDescription: input.companyDescription,
    portal: input.portal,
    documents: input.documents,
    email: input.email,
    domains: input.domains,
    toggles: input.toggles,
    sourceOnboardingId: input.sourceOnboardingId || null,
    events: [
      {
        type: "Brand Created",
        createdAt: now,
        summary: `${input.companyName} branding profile created.`,
      },
    ],
    createdAt: now,
    updatedAt: now,
  });
}

export function resolveBusinessCenterBrandingProfile(
  companyId?: BusinessCenterEntityId | null,
  companySlug?: string | null,
  companyName?: string | null,
  brand?: string | null,
): BusinessCenterBrandingProfileRecord | null {
  const profiles = loadBusinessCenterBrandingProfiles();
  if (companyId) {
    const byId = profiles.find((profile) => profile.companyId === companyId || profile.id === companyId);
    if (byId) return byId;
  }
  if (companySlug) {
    const bySlug = profiles.find((profile) => slugify(profile.companyName) === slugify(companySlug) || slugify(profile.publicDisplayName) === slugify(companySlug));
    if (bySlug) return bySlug;
  }
  if (brand) {
    const byBrand = profiles.find((profile) => profile.domains.customSubdomain.includes(brand) || profile.publicDisplayName.toLowerCase().includes(brand.toLowerCase()));
    if (byBrand) return byBrand;
  }
  if (companyName) {
    const byName = profiles.find((profile) => slugify(profile.companyName) === slugify(companyName) || slugify(profile.publicDisplayName) === slugify(companyName));
    if (byName) return byName;
  }
  return profiles[0] || null;
}

export function buildBusinessCenterBrandingPreview(profile: BusinessCenterBrandingProfileRecord): BusinessCenterBrandingPreviewSnapshot {
  return {
    portal: {
      title: profile.portal.name,
      subtitle: profile.portal.subtitle,
      welcomeMessage: profile.portal.welcomeMessage,
      colors: {
        primary: profile.portal.primaryColor,
        secondary: profile.portal.secondaryColor,
        accent: profile.portal.accentColor,
      },
    },
    login: {
      title: profile.publicDisplayName,
      subtitle: profile.companyDescription,
      accent: profile.accentColor,
    },
    dashboard: {
      title: profile.publicDisplayName,
      subtitle: `${profile.companyName} workspace`,
      badge: profile.toggles.showPoweredByBusinessCenterOS ? "Powered by Business Center OS" : "White-label enabled",
    },
    email: {
      subject: `${profile.publicDisplayName} update`,
      headerColor: profile.email.headerColor,
      footerColor: profile.email.footerColor,
      signature: profile.email.signature,
    },
  };
}

export function buildBusinessCenterBrandingDashboard(records: BusinessCenterBrandingProfileRecord[]): BusinessCenterBrandingDashboard {
  return {
    totalProfiles: records.length,
    activeDomains: records.filter((record) => Boolean(record.domains.primaryDomain || record.domains.portalDomain || record.domains.customSubdomain)).length,
    portalReady: records.filter((record) => Boolean(record.portal.name.trim()) && Boolean(record.portal.welcomeMessage.trim())).length,
    logoReady: records.filter((record) => Boolean(record.logoUrl.trim()) && Boolean(record.faviconUrl.trim())).length,
    hidePoweredByCount: records.filter((record) => !record.toggles.showPoweredByBusinessCenterOS).length,
  };
}

function createSeedBrandingProfiles(): BusinessCenterBrandingProfileRecord[] {
  const companies = loadBusinessCenterCompanies().filter((company) => company.companyType !== "parent");
  const onboarding = loadBusinessCenterOnboardingRecords();
  const now = new Date().toISOString();

  return companies.map((company, index) => {
    const onboardingRecord = onboarding.find((record) => record.companyId === company.id) || null;
    return normalizeBrandingProfile(
      createBusinessCenterBrandingProfile({
        companyId: company.id,
        companyName: company.companyName,
        companySlug: company.companySlug,
        publicDisplayName: onboardingRecord?.branding.companyName || company.companyName,
        logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
        faviconUrl: `${company.companySlug}-favicon.ico`,
        primaryColor: onboardingRecord?.branding.primaryColor || resolveBrandPrimaryColor(company),
        secondaryColor: onboardingRecord?.branding.accentColor || resolveBrandSecondaryColor(company),
        accentColor: onboardingRecord?.branding.accentColor || resolveBrandAccentColor(company),
        contactEmail: onboardingRecord?.branding.contactEmail || `hello@${company.companySlug}.com`,
        supportEmail: `support@${company.companySlug}.com`,
        phoneNumber: onboardingRecord?.branding.contactPhone || "",
        website: `https://${company.companySlug}.com`,
        companyDescription: buildDescription(company),
        portal: {
          name: `${onboardingRecord?.branding.companyName || company.companyName} Portal`,
          subtitle: `Client portal for ${company.companyName}`,
          logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
          primaryColor: onboardingRecord?.branding.primaryColor || resolveBrandPrimaryColor(company),
          secondaryColor: onboardingRecord?.branding.accentColor || resolveBrandSecondaryColor(company),
          accentColor: onboardingRecord?.branding.accentColor || resolveBrandAccentColor(company),
          welcomeMessage: onboardingRecord?.notes || "Welcome to your branded Business Center portal.",
        },
        documents: {
          logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
          footerText: `${company.companyName} • Confidential client materials`,
          signatureBlock: `${company.companyName}\nBusiness Center OS`,
          companyAddress: `${company.companyName} headquarters`,
          companyContactInformation: onboardingRecord?.branding.contactEmail || `hello@${company.companySlug}.com`,
        },
        email: {
          logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
          headerColor: onboardingRecord?.branding.primaryColor || resolveBrandPrimaryColor(company),
          footerColor: onboardingRecord?.branding.accentColor || resolveBrandAccentColor(company),
          signature: `${company.companyName} Team`,
          companyFooter: `${company.companyName} is powered by Business Center OS.`,
        },
        domains: {
          primaryDomain: `${company.companySlug}.com`,
          portalDomain: `portal.${company.companySlug}.com`,
          customSubdomain: `${company.companySlug}.business`,
          loginUrl: `https://${company.companySlug}.com/login`,
        },
        toggles: {
          showPoweredByBusinessCenterOS: index % 2 === 0,
          showWorkspaceName: true,
          showCompanyName: true,
        },
        sourceOnboardingId: onboardingRecord?.id || null,
      }),
    );
  });
}

function appendBrandingEvents(
  existingEvents: BusinessCenterBrandingEventRecord[],
  existingProfile: BusinessCenterBrandingProfileRecord | null,
  nextProfile: Partial<BusinessCenterBrandingProfileRecord>,
): BusinessCenterBrandingEventRecord[] {
  const type = detectBrandingEventType(existingProfile, nextProfile);
  const summary = buildEventSummary(type, existingProfile, nextProfile);
  if (!summary) return existingEvents;
  return [
    ...existingEvents,
    {
      type,
      createdAt: new Date().toISOString(),
      summary,
    },
  ];
}

function detectBrandingEventType(
  existingProfile: BusinessCenterBrandingProfileRecord | null,
  nextProfile: Partial<BusinessCenterBrandingProfileRecord>,
): BusinessCenterBrandingEventRecord["type"] {
  if (!existingProfile) return "Brand Created";
  if (nextProfile.domains && JSON.stringify(nextProfile.domains) !== JSON.stringify(existingProfile.domains)) return "Domain Updated";
  if (nextProfile.portal && JSON.stringify(nextProfile.portal) !== JSON.stringify(existingProfile.portal)) return "Portal Updated";
  if (
    nextProfile.logoUrl !== undefined ||
    nextProfile.faviconUrl !== undefined ||
    nextProfile.primaryColor !== undefined ||
    nextProfile.secondaryColor !== undefined ||
    nextProfile.accentColor !== undefined
  ) {
    if (nextProfile.logoUrl !== undefined || nextProfile.faviconUrl !== undefined) return "Logo Updated";
    return "Brand Updated";
  }
  return "Brand Updated";
}

function buildEventSummary(
  type: BusinessCenterBrandingEventRecord["type"],
  existingProfile: BusinessCenterBrandingProfileRecord | null,
  nextProfile: Partial<BusinessCenterBrandingProfileRecord>,
): string {
  const name = nextProfile.companyName || existingProfile?.companyName || "Brand";
  switch (type) {
    case "Brand Created":
      return `${name} branding profile created.`;
    case "Logo Updated":
      return `${name} logo or favicon updated.`;
    case "Portal Updated":
      return `${name} portal branding updated.`;
    case "Domain Updated":
      return `${name} domain settings updated.`;
    default:
      return `${name} branding profile updated.`;
  }
}

function normalizeBrandingProfile(profile: BusinessCenterBrandingProfileRecord): BusinessCenterBrandingProfileRecord {
  return {
    ...profile,
    id: profile.id || `branding-${profile.companyId}`,
    companyId: profile.companyId,
    companyName: profile.companyName || "Untitled Company",
    publicDisplayName: profile.publicDisplayName || profile.companyName || "Untitled Company",
    logoUrl: profile.logoUrl || "",
    faviconUrl: profile.faviconUrl || "",
    primaryColor: profile.primaryColor || "#1F2937",
    secondaryColor: profile.secondaryColor || "#334155",
    accentColor: profile.accentColor || "#06B6D4",
    contactEmail: profile.contactEmail || "hello@example.com",
    supportEmail: profile.supportEmail || "support@example.com",
    phoneNumber: profile.phoneNumber || "",
    website: profile.website || "",
    companyDescription: profile.companyDescription || "",
    portal: {
      name: profile.portal?.name || `${profile.publicDisplayName || profile.companyName} Portal`,
      subtitle: profile.portal?.subtitle || "Client portal",
      logoUrl: profile.portal?.logoUrl || profile.logoUrl || "",
      primaryColor: profile.portal?.primaryColor || profile.primaryColor || "#1F2937",
      secondaryColor: profile.portal?.secondaryColor || profile.secondaryColor || "#334155",
      accentColor: profile.portal?.accentColor || profile.accentColor || "#06B6D4",
      welcomeMessage: profile.portal?.welcomeMessage || "",
    },
    documents: {
      logoUrl: profile.documents?.logoUrl || profile.logoUrl || "",
      footerText: profile.documents?.footerText || "",
      signatureBlock: profile.documents?.signatureBlock || "",
      companyAddress: profile.documents?.companyAddress || "",
      companyContactInformation: profile.documents?.companyContactInformation || profile.contactEmail || "",
    },
    email: {
      logoUrl: profile.email?.logoUrl || profile.logoUrl || "",
      headerColor: profile.email?.headerColor || profile.primaryColor || "#1F2937",
      footerColor: profile.email?.footerColor || profile.secondaryColor || "#334155",
      signature: profile.email?.signature || "",
      companyFooter: profile.email?.companyFooter || "",
    },
    domains: {
      primaryDomain: profile.domains?.primaryDomain || "",
      portalDomain: profile.domains?.portalDomain || "",
      customSubdomain: profile.domains?.customSubdomain || "",
      loginUrl: profile.domains?.loginUrl || "",
    },
    toggles: {
      showPoweredByBusinessCenterOS: Boolean(profile.toggles?.showPoweredByBusinessCenterOS),
      showWorkspaceName: profile.toggles?.showWorkspaceName !== false,
      showCompanyName: profile.toggles?.showCompanyName !== false,
    },
    sourceOnboardingId: profile.sourceOnboardingId || null,
    events: Array.isArray(profile.events) ? profile.events : [],
    createdAt: profile.createdAt || new Date().toISOString(),
    updatedAt: profile.updatedAt || new Date().toISOString(),
  };
}

function resolveBrandPrimaryColor(company: BusinessCenterCompanyRecord) {
  if (company.brand === "kreatethedate") return "#FF6B35";
  if (company.brand === "ar_recovery") return "#7C3AED";
  return "#06B6D4";
}

function resolveBrandSecondaryColor(company: BusinessCenterCompanyRecord) {
  if (company.brand === "kreatethedate") return "#14B8A6";
  if (company.brand === "ar_recovery") return "#EC4899";
  return "#94A3B8";
}

function resolveBrandAccentColor(company: BusinessCenterCompanyRecord) {
  if (company.brand === "kreatethedate") return "#F59E0B";
  if (company.brand === "ar_recovery") return "#A855F7";
  return "#22C55E";
}

function buildDescription(company: BusinessCenterCompanyRecord) {
  if (company.brand === "kreatethedate") return "Luxury event client experience with RSVP, Memory, Archive, and Memory Media Vault branding.";
  if (company.brand === "ar_recovery") return "White-label recovery operations workspace for tenants and collectors.";
  return "Consulting client portal and delivery workspace with shared Business Center branding.";
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "brand";
}
